import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowLeft, ArrowRight, Check, Search, Camera, Loader2, Plus, Star, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDistanceKm } from "@/lib/format";
import { CLUBS } from "@/data/clubs";
import { FAN_TYPES } from "@/data/fanTypes";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";

const photoSrc = (url) => (url && url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);
const MAX_PHOTOS = 6;

const CONNECTION_OPTIONS = [
  { id: "dating",     label: "Dating",             desc: "Open to a relationship" },
  { id: "friendship", label: "Friendship",         desc: "Mates who share the passion" },
  { id: "matchday",   label: "Matchday companion", desc: "Someone to go to the game with" },
  { id: "fivesaside", label: "Five-a-side",        desc: "Weekly kickabout / Sunday league" },
  { id: "prematch",   label: "Pre-match pub",      desc: "A pint and a chat before kick-off" },
  { id: "away",       label: "Away travel buddy", desc: "Split the trip to away days" },
];
const GENDERS = ["Man", "Woman", "Non-binary", "Other"];
const ORIENTATIONS = ["Straight", "Gay", "Lesbian", "Bisexual", "Pansexual", "Queer", "Asexual", "Prefer not to say"];
const INTERESTED = ["Men", "Women", "Non-binary", "Everyone"];

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);  const [f, setF] = useState({
    name: user?.name || "",
    age: 25,
    dob: "",
    gender: "",
    sexual_orientation: "",
    interested_in: [],
    location: "",
    club: "",
    connection_types: [],
    fan_type: [],
    bio: "",
    prompts: [],                // list of { prompt, answer } — max 3
    banter_tolerance: 60,       // 0–100 slider
    photo_url: "",
    photos: [],
    open_to_rival_fans: true,
    min_age: 18,
    max_age: 60,
    postcode: "",
    max_distance_km: 50,
  });
  const [promptLibrary, setPromptLibrary] = useState([]);

  // Load the canonical prompt library from the backend once.
  useEffect(() => {
    api.get("/profile/prompts").then(r => setPromptLibrary(r.data?.prompts || [])).catch(() => {});
  }, []);

  // Prompt helpers — pick/unpick/edit up to 3 prompts.
  const selectedPrompts = f.prompts || [];
  const isPromptSelected = (p) => selectedPrompts.some(x => x.prompt === p);
  const togglePrompt = (p) => {
    setF(prev => {
      const cur = prev.prompts || [];
      if (cur.some(x => x.prompt === p)) return { ...prev, prompts: cur.filter(x => x.prompt !== p) };
      if (cur.length >= 3) return prev;  // hard cap
      return { ...prev, prompts: [...cur, { prompt: p, answer: "" }] };
    });
  };
  const updatePromptAnswer = (p, ans) => {
    setF(prev => ({
      ...prev,
      prompts: (prev.prompts || []).map(x => x.prompt === p ? { ...x, answer: ans.slice(0, 200) } : x),
    }));
  };
  const [clubSearch, setClubSearch] = useState("");
  const [customClubMode, setCustomClubMode] = useState(false);
  const [customClub, setCustomClub] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [postcodeStatus, setPostcodeStatus] = useState("idle"); // idle|checking|ok|invalid
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileRef = useRef(null);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("That photo is over 8MB. Please pick a smaller one.");
      return;
    }
    if ((f.photos?.length || 0) >= MAX_PHOTOS) {
      toast.error(`Max ${MAX_PHOTOS} photos. Remove one first.`);
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const nextPhotos = Array.isArray(data?.photos) && data.photos.length ? data.photos : [...(f.photos || []), data?.url].filter(Boolean);
      setF((prev) => ({ ...prev, photos: nextPhotos, photo_url: nextPhotos[0] || "" }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Photo upload failed");
    } finally {
      setUploadingPhoto(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = async (index) => {
    try {
      const { data } = await api.delete("/upload/photo", { data: { index } });
      const next = Array.isArray(data?.photos) ? data.photos : [];
      setF((prev) => ({ ...prev, photos: next, photo_url: next[0] || "" }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove photo");
    }
  };

  const makePrimary = async (index) => {
    try {
      const { data } = await api.post("/upload/photo/primary", { index });
      const next = Array.isArray(data?.photos) ? data.photos : [];
      setF((prev) => ({ ...prev, photos: next, photo_url: next[0] || "" }));
      toast.success("Primary photo updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not set primary");
    }
  };

  // Pre-fill from existing profile so /onboarding also works as "edit profile"
  useEffect(() => {
    const p = user?.profile;
    if (!p || Object.keys(p).length === 0) return;
    setF((prev) => ({
      ...prev,
      name: user.name || prev.name,
      age: p.age ?? prev.age,
      dob: p.dob || prev.dob,
      gender: (p.gender && p.gender[0].toUpperCase() + p.gender.slice(1)) || prev.gender,
      sexual_orientation: (p.sexual_orientation && p.sexual_orientation[0].toUpperCase() + p.sexual_orientation.slice(1)) || prev.sexual_orientation,
      interested_in: (p.interested_in || []).map(v => v[0].toUpperCase() + v.slice(1)),
      location: p.location || prev.location,
      club: p.club || prev.club,
      connection_types: p.connection_types || prev.connection_types,
      fan_type: p.fan_type || prev.fan_type,
      bio: p.bio || prev.bio,
      photo_url: p.photo_url || prev.photo_url,
      photos: (p.photos && p.photos.length) ? p.photos : (p.photo_url ? [p.photo_url] : prev.photos),
      open_to_rival_fans: p.open_to_rival_fans ?? prev.open_to_rival_fans,
      min_age: p.min_age ?? prev.min_age,
      max_age: p.max_age ?? prev.max_age,
      postcode: p.postcode || prev.postcode,
      max_distance_km: p.max_distance_km ?? prev.max_distance_km,
    }));
    if (p.postcode) setPostcodeStatus("ok");
    // If the saved club is not in our master list, it was entered as a custom non-league club.
    if (p.club && !CLUBS.some(c => c.name === p.club)) {
      setCustomClub(p.club);
      setCustomClubMode(true);
    }
  }, [user]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (k, v) => setF((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter(x=>x!==v) : [...p[k], v] }));

  const clubs = useMemo(() => {
    const q = clubSearch.toLowerCase().trim();
    return q ? CLUBS.filter(c => c.name.toLowerCase().includes(q)) : CLUBS;
  }, [clubSearch]);

  // Group clubs by league so a 180-strong list stays scannable
  const clubsByLeague = useMemo(() => {
    const map = new Map();
    for (const c of clubs) {
      if (!map.has(c.league)) map.set(c.league, []);
      map.get(c.league).push(c);
    }
    return Array.from(map.entries()); // preserves insertion order (Premier League first)
  }, [clubs]);

  const steps = [
    { key: "connection", title: "What are you here for?", subtitle: "Pick one or more — you can change this later." },
    { key: "club", title: "Who do you support?", subtitle: "Your club matters — and how you follow them matters more." },
    { key: "identity", title: "About you", subtitle: "So we can find your people." },
    { key: "location", title: "Where do you follow the game from?", subtitle: "Town or city in the UK." },
    { key: "bio", title: "Write your bio", subtitle: "Or let AI do it for you." },
  ];

  const computeAge = (dob) => {
    if (!dob) return null;
    const d = new Date(dob);
    if (isNaN(d.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
    return age;
  };
  const derivedAge = computeAge(f.dob);
  const todayIso = new Date().toISOString().slice(0, 10);
  const maxDobIso = (() => { const d = new Date(); d.setFullYear(d.getFullYear() - 18); return d.toISOString().slice(0, 10); })();

  const canNext = () => {
    if (step === 0) return f.connection_types.length > 0;
    if (step === 1) {
      const hasClub = customClubMode ? customClub.trim().length >= 2 : !!f.club;
      return hasClub && (f.fan_type?.length || 0) >= 1;
    }
    if (step === 2) return f.gender && f.sexual_orientation && f.interested_in.length > 0 && derivedAge !== null && derivedAge >= 18 && derivedAge <= 120;
    if (step === 3) return f.location.trim().length > 1;
    if (step === 4) {
      // New requirement: pick at least 1 prompt AND write a non-trivial answer for each chosen prompt.
      // Bio field is now legacy / hidden — no requirement on it.
      const chosen = (f.prompts || []).filter(p => (p.answer || "").trim().length >= 3);
      return chosen.length >= 1;
    }
    return true;
  };

  const genBio = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/generate-bio", {
        club: f.club,
        connection_types: f.connection_types,
        interests: "",
        tone: "friendly and confident",
      });
      set("bio", data.bio);
      toast.success("Bio generated!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI bio failed");
    } finally { setAiLoading(false); }
  };

  const finish = async () => {
    setSaving(true);
    try {
      const payload = {
        ...f,
        age: derivedAge && derivedAge >= 18 ? derivedAge : f.age,
        club: customClubMode ? customClub.trim() : f.club,
        gender: f.gender.toLowerCase(),
        sexual_orientation: f.sexual_orientation.toLowerCase(),
        interested_in: f.interested_in.map(v => v === "Everyone" ? "everyone" : v.toLowerCase()),
        photos: f.photos || [],
        photo_url: (f.photos && f.photos[0]) || f.photo_url || "",
      };
      // Expand "Everyone" so matching works
      if (payload.interested_in.includes("everyone")) payload.interested_in = ["man","woman","non-binary","other"];
      await api.put("/profile", payload);
      await refresh();
      toast.success("Profile saved!");
      nav("/discover", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save profile");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-footy-bg text-white grain">
      <div className="max-w-xl mx-auto px-5 pt-8 pb-24">
        {/* Progress */}
        <div className="flex gap-2 mb-8">
          {steps.map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-footy-red" : "bg-white/10"}`} />
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={step}
            initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.25 }}>

            <p className="text-xs uppercase tracking-[0.2em] text-footy-red font-bold">Step {step+1} of {steps.length}</p>
            <h1 className="font-display text-4xl sm:text-5xl uppercase mt-2 leading-none tracking-tight">{steps[step].title}</h1>
            <p className="text-slate-400 mt-3">{steps[step].subtitle}</p>

            <div className="mt-8">
              {step === 0 && (
                <div className="grid gap-3" data-testid="step-connection">
                  {CONNECTION_OPTIONS.map(o => (
                    <button key={o.id} type="button"
                      data-testid={`connection-${o.id}`}
                      onClick={()=>toggle("connection_types", o.id)}
                      className={`w-full text-left p-5 rounded-2xl border transition-all ${f.connection_types.includes(o.id) ? "border-footy-red bg-footy-red/10" : "border-white/15 bg-white/5 hover:bg-white/10"}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-display text-2xl uppercase tracking-wide">{o.label}</div>
                          <div className="text-sm text-slate-400 mt-1">{o.desc}</div>
                        </div>
                        {f.connection_types.includes(o.id) && <Check className="text-footy-red" />}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {step === 1 && (
                <div data-testid="step-club">
                  {!customClubMode ? (
                    <>
                      <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input value={clubSearch} onChange={(e)=>setClubSearch(e.target.value)}
                          placeholder="Search 150+ clubs — Premier League to National League..." className="footy-input pl-11" data-testid="club-search" />
                      </div>
                      <p className="text-xs text-slate-500 mt-2" data-testid="club-count">
                        {clubs.length} club{clubs.length === 1 ? "" : "s"}{clubSearch.trim() ? ` matching "${clubSearch.trim()}"` : ""}
                      </p>
                      <div className="mt-4 max-h-[380px] overflow-y-auto pr-1 space-y-5">
                        {clubsByLeague.length === 0 && (
                          <p className="text-sm text-slate-400 py-6 text-center">No clubs match — try a different spelling, or use the non-league option below.</p>
                        )}
                        {clubsByLeague.map(([league, list]) => (
                          <div key={league}>
                            <p className="text-[10px] uppercase tracking-widest text-footy-red font-bold mb-2">{league}</p>
                            <div className="flex flex-wrap gap-2">
                              {list.map(c => (
                                <button key={c.name} type="button" data-testid={`club-${c.name}`}
                                  onClick={()=>set("club", c.name)}
                                  className={f.club === c.name ? "footy-chip-active" : "footy-chip"}>
                                  {c.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      <button type="button"
                        onClick={()=>{ setCustomClubMode(true); set("club", ""); }}
                        className="mt-5 w-full text-sm text-slate-300 hover:text-white border border-dashed border-white/20 rounded-2xl py-3 hover:border-footy-red/60 transition-colors"
                        data-testid="non-league-toggle">
                        Non-league fan? <span className="text-footy-red font-semibold">Type your club name →</span>
                      </button>
                    </>
                  ) : (
                    <div className="space-y-3" data-testid="custom-club-input-wrap">
                      <label className="footy-label">Your club</label>
                      <input value={customClub} onChange={(e)=>setCustomClub(e.target.value)}
                        placeholder="e.g. Kettering Town, Alsager Town, Nantwich Town..."
                        className="footy-input" maxLength={60} autoFocus data-testid="custom-club-input" />
                      <p className="text-xs text-slate-500">
                        For clubs playing below National League South (Steps 3 and below in the pyramid), or historic clubs, or your Sunday-league side. Keep it short and easy to recognise.
                      </p>
                      <button type="button"
                        onClick={()=>{ setCustomClubMode(false); setCustomClub(""); }}
                        className="text-sm text-slate-400 hover:text-white underline"
                        data-testid="custom-club-cancel">
                        ← Back to the club list
                      </button>
                    </div>
                  )}
                  <div className="mt-8" data-testid="step-fan-type">
                    <label className="footy-label">How do you follow your club? <span className="text-slate-500 text-xs normal-case tracking-normal">(pick 1+)</span></label>
                    <p className="text-xs text-slate-400 mt-1 mb-3">
                      Every kind of fan counts. Armchair loyal Reds match armchair loyal Blues just as easily as season-ticket lot.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {FAN_TYPES.map(ft => {
                        const on = (f.fan_type || []).includes(ft.id);
                        return (
                          <button key={ft.id} type="button"
                            data-testid={`fan-type-${ft.id}`}
                            onClick={() => toggle("fan_type", ft.id)}
                            className={on ? "footy-chip-active" : "footy-chip"}>
                            {ft.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <label className="flex items-center gap-3 mt-6 text-sm text-slate-300 cursor-pointer">
                    <input type="checkbox" checked={f.open_to_rival_fans}
                      onChange={(e)=>set("open_to_rival_fans", e.target.checked)}
                      className="w-5 h-5 accent-footy-red" data-testid="open-to-rivals" />
                    I'm open to matching with rival fans (banter welcome)
                  </label>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6" data-testid="step-identity">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="footy-label !mb-0">Date of birth</label>
                      {derivedAge !== null && (
                        <span className="font-display text-lg text-footy-red leading-none" data-testid="own-age-value">
                          {derivedAge < 18 ? "Under 18" : `${derivedAge} yrs`}
                        </span>
                      )}
                    </div>
                    <input type="date" value={f.dob} max={maxDobIso}
                      onChange={(e)=>set("dob", e.target.value)}
                      className="footy-input" data-testid="input-dob" />
                    <p className="text-xs text-slate-500 mt-2">
                      We show your age (not your date of birth) to other users. You must be 18+.
                    </p>
                    {f.dob && derivedAge !== null && derivedAge < 18 && (
                      <p className="text-xs text-footy-red mt-2">Footymatch is 18+.</p>
                    )}
                  </div>
                  <div>
                    <label className="footy-label">You identify as</label>
                    <div className="flex flex-wrap gap-2">
                      {GENDERS.map(g => (
                        <button key={g} type="button" data-testid={`gender-${g}`}
                          onClick={()=>set("gender", g)}
                          className={f.gender === g ? "footy-chip-active" : "footy-chip"}>{g}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="footy-label">Orientation</label>
                    <div className="flex flex-wrap gap-2">
                      {ORIENTATIONS.map(g => (
                        <button key={g} type="button" data-testid={`orientation-${g}`}
                          onClick={()=>set("sexual_orientation", g)}
                          className={f.sexual_orientation === g ? "footy-chip-active" : "footy-chip"}>{g}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="footy-label">Interested in</label>
                    <div className="flex flex-wrap gap-2">
                      {INTERESTED.map(g => (
                        <button key={g} type="button" data-testid={`interested-${g}`}
                          onClick={()=>toggle("interested_in", g)}
                          className={f.interested_in.includes(g) ? "footy-chip-active" : "footy-chip"}>{g}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="footy-label !mb-0">Show me people aged</label>
                      <span className="font-display text-lg text-footy-red leading-none" data-testid="age-range-label">
                        {f.min_age} – {f.max_age}
                      </span>
                    </div>
                    <Slider min={18} max={99} step={1} minStepsBetweenThumbs={1}
                      value={[f.min_age, f.max_age]}
                      onValueChange={([lo, hi]) => setF((p) => ({ ...p, min_age: lo, max_age: hi }))}
                      data-testid="input-age-range" />
                    <div className="flex justify-between text-[10px] text-slate-500 mt-2 uppercase tracking-widest"><span>18</span><span>99</span></div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div data-testid="step-location" className="space-y-5">
                  <div>
                    <label className="footy-label">Town / city</label>
                    <input value={f.location} onChange={(e)=>set("location", e.target.value)}
                      placeholder="e.g. Manchester" className="footy-input" data-testid="input-location" />
                  </div>
                  <div>
                    <label className="footy-label">UK postcode <span className="text-slate-500 text-xs normal-case tracking-normal">(optional — unlocks distance matching)</span></label>
                    <div className="relative">
                      <input value={f.postcode}
                        onChange={(e)=>{ set("postcode", e.target.value.toUpperCase()); setPostcodeStatus("idle"); }}
                        onBlur={async ()=>{
                          const pc = (f.postcode || "").replace(/\s+/g, "");
                          if (!pc) { setPostcodeStatus("idle"); return; }
                          setPostcodeStatus("checking");
                          try {
                            const resp = await fetch(`https://api.postcodes.io/postcodes/${pc}/validate`);
                            const j = await resp.json();
                            setPostcodeStatus(j?.result ? "ok" : "invalid");
                          } catch { setPostcodeStatus("idle"); }
                        }}
                        placeholder="e.g. M14 6RA"
                        className="footy-input uppercase pr-24"
                        maxLength={8}
                        data-testid="input-postcode" />
                      <span className={`absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold uppercase tracking-widest ${
                        postcodeStatus === "ok" ? "text-emerald-400" :
                        postcodeStatus === "invalid" ? "text-footy-red" :
                        postcodeStatus === "checking" ? "text-slate-400" : "text-transparent"}`}
                        data-testid="postcode-status">
                        {postcodeStatus === "ok" ? "Valid" : postcodeStatus === "invalid" ? "Not found" : postcodeStatus === "checking" ? "..." : ""}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">We only use your postcode to show distance. Never shown to other users.</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="footy-label !mb-0">Max distance</label>
                      <span className="text-sm text-footy-red font-bold">{formatDistanceKm(f.max_distance_km)}</span>
                    </div>
                    <input type="range" min={5} max={200} step={5}
                      value={f.max_distance_km}
                      onChange={(e)=>set("max_distance_km", parseInt(e.target.value))}
                      className="w-full mt-3 accent-footy-red"
                      data-testid="input-max-distance" />
                    <div className="flex justify-between text-[10px] text-slate-500 mt-1 uppercase tracking-widest"><span>5 km (3 mi)</span><span>200 km (124 mi)</span></div>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div data-testid="step-bio" className="space-y-6">
                  <div>
                    <label className="footy-label">Pick 3 prompts to answer <span className="text-slate-500 text-xs normal-case tracking-normal">({selectedPrompts.length}/3)</span></label>
                    <p className="text-xs text-slate-400 mt-1">Skip the generic bio — these tell people who you REALLY are as a fan.</p>
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="prompt-library">
                      {promptLibrary.map((p) => {
                        const on = isPromptSelected(p);
                        const disabled = !on && selectedPrompts.length >= 3;
                        return (
                          <button key={p} type="button"
                            onClick={()=>togglePrompt(p)}
                            disabled={disabled}
                            data-testid={`prompt-chip-${p.slice(0,20).replace(/[^a-z0-9]/gi,'-').toLowerCase()}`}
                            className={`text-xs font-medium px-3 py-2 rounded-full border transition-colors ${on ? "bg-footy-red border-footy-red text-white" : disabled ? "bg-white/5 border-white/10 text-slate-500 cursor-not-allowed" : "bg-white/5 border-white/20 text-slate-200 hover:bg-white/10"}`}>
                            {p}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {selectedPrompts.length > 0 && (
                    <div className="space-y-3" data-testid="prompt-answers">
                      {selectedPrompts.map((pa) => (
                        <div key={pa.prompt} className="rounded-xl bg-white/5 border border-white/10 p-3">
                          <div className="text-[11px] uppercase tracking-widest text-footy-red font-bold">{pa.prompt}</div>
                          <textarea value={pa.answer}
                            onChange={(e)=>updatePromptAnswer(pa.prompt, e.target.value)}
                            rows={2} maxLength={200}
                            placeholder="Your answer…"
                            className="footy-input resize-none mt-2 !text-sm"
                            data-testid={`prompt-answer-${pa.prompt.slice(0,20).replace(/[^a-z0-9]/gi,'-').toLowerCase()}`} />
                          <div className="text-[10px] text-slate-500 text-right">{pa.answer.length}/200</div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="rounded-xl bg-white/5 border border-white/10 p-4" data-testid="banter-tolerance-panel">
                    <label className="footy-label !mb-1">Rival banter tolerance</label>
                    <p className="text-xs text-slate-400">How much stick can you take from rival fans?</p>
                    <div className="mt-3 flex items-center justify-between text-[11px] uppercase tracking-widest text-slate-500">
                      <span>Get furious</span>
                      <span className="text-footy-red font-bold text-lg normal-case tracking-normal">{f.banter_tolerance}%</span>
                      <span>Bring it on</span>
                    </div>
                    <input type="range" min={0} max={100} step={5}
                      value={f.banter_tolerance}
                      onChange={(e)=>set("banter_tolerance", parseInt(e.target.value))}
                      className="w-full accent-footy-red mt-1" data-testid="banter-slider" />
                  </div>

                  <div>
                    <label className="footy-label">Profile photos <span className="text-slate-500 text-xs normal-case tracking-normal">(add up to {MAX_PHOTOS} — first one is your primary)</span></label>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden"
                      onChange={uploadPhoto} data-testid="input-photo-file" />
                    <PhotoGrid
                      photos={f.photos || []}
                      uploading={uploadingPhoto}
                      onAdd={()=>fileRef.current?.click()}
                      onRemove={removePhoto}
                      onSetPrimary={makePrimary}
                    />
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Nav */}
        <div className="mt-10 flex gap-3">
          {step > 0 && (
            <button type="button" onClick={()=>setStep(s=>s-1)}
              className="footy-btn-secondary !flex-none !w-14 !p-0 h-14 rounded-full"
              data-testid="onboarding-back">
              <ArrowLeft size={20} />
            </button>
          )}
          {step < steps.length - 1 ? (
            <button type="button" onClick={()=>{
              // Fire progress tracking (fire-and-forget) so /admin/analytics knows the user reached the NEXT step
              api.post("/onboarding-progress", { step: step + 1 }).catch(() => {});
              // If NEXT step is the final (bio) step, also record step=5 (= reached bio) since that
              // step ends with a Finish button (no Continue click), otherwise the funnel undercount.
              if (step + 1 === steps.length - 1) {
                api.post("/onboarding-progress", { step: 5 }).catch(() => {});
              }
              setStep(s=>s+1);
            }}
              disabled={!canNext()}
              className="footy-btn-primary flex-1" data-testid="onboarding-next">
              Continue <ArrowRight size={18} />
            </button>
          ) : (
            <button type="button" onClick={finish} disabled={!canNext() || saving}
              className="footy-btn-primary flex-1" data-testid="onboarding-finish">
              {saving ? "Saving..." : "Enter the ground"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function PhotoGrid({ photos = [], uploading, onAdd, onRemove, onSetPrimary }) {
  const slots = Array.from({ length: MAX_PHOTOS }, (_, i) => photos[i] || null);
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="photo-grid">
      {slots.map((url, i) => {
        if (url) {
          const isPrimary = i === 0;
          return (
            <div key={`p-${i}`} className={`relative aspect-[3/4] rounded-2xl overflow-hidden border ${isPrimary ? "border-footy-red/60 ring-2 ring-footy-red/40" : "border-white/10"}`} data-testid={`photo-slot-${i}`}>
              <img src={photoSrc(url)} alt="" className="w-full h-full object-cover" />
              {isPrimary && (
                <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-footy-red text-white">
                  <Star size={9} fill="currentColor" /> Primary
                </span>
              )}
              <button type="button" onClick={()=>onRemove(i)}
                className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center"
                data-testid={`photo-remove-${i}`} title="Remove">
                <X size={14} />
              </button>
              {!isPrimary && (
                <button type="button" onClick={()=>onSetPrimary(i)}
                  className="absolute bottom-1.5 left-1.5 right-1.5 py-1.5 rounded-full bg-black/70 hover:bg-black text-white text-[10px] font-bold uppercase tracking-widest flex items-center justify-center gap-1"
                  data-testid={`photo-set-primary-${i}`}>
                  <Star size={10} /> Make primary
                </button>
              )}
            </div>
          );
        }
        // First empty slot is the "add" tile
        const isFirstEmpty = i === photos.length;
        return (
          <button key={`e-${i}`} type="button"
            onClick={isFirstEmpty ? onAdd : undefined}
            disabled={!isFirstEmpty || uploading}
            className={`aspect-[3/4] rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-1 text-slate-400 transition-colors ${isFirstEmpty ? "border-white/15 hover:border-footy-red/60 cursor-pointer" : "border-white/5 cursor-not-allowed opacity-40"}`}
            data-testid={`photo-add-${i}`}>
            {isFirstEmpty ? (
              uploading ? <Loader2 size={20} className="animate-spin text-footy-red" /> : (
                <>
                  <Plus size={20} className="text-footy-red" />
                  <span className="text-[10px] uppercase tracking-widest font-bold">{photos.length === 0 ? "Add photo" : "Add"}</span>
                </>
              )
            ) : (
              <span className="text-[10px] uppercase tracking-widest">Slot {i + 1}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
